import {
  Badge,
  Button,
  Card,
  Title2,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Copy16Filled,
  Copy16Regular,
  Checkmark16Filled,
  Dismiss16Filled,
} from '@fluentui/react-icons';
import DOMPurify from 'dompurify';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard/useCopyToClipboard';
import {
  renderMarkdown,
  isMarkdownContent,
} from '../../../utils/renderMarkdown';
import { getSkillPassCount, getEvalAverage } from '../../../utils/sigsParser';

import type { InteractionSummaryProps } from './InteractionSummary.interface';
import { useStyles } from './InteractionSummary.style';

const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;

function InlineCopy({ value, label }: { value: string; label: string }) {
  const styles = useStyles();
  const { copied, copy } = useCopyToClipboard();

  return (
    <Tooltip
      content={copied ? 'Copied!' : `Copy ${label}`}
      relationship="label"
    >
      <Button
        className={styles.copyBtn}
        appearance="subtle"
        size="small"
        icon={<CopyIcon />}
        onClick={() => copy(value)}
        aria-label={`Copy ${label} to clipboard`}
      />
    </Tooltip>
  );
}

function agentBadgeColor(
  agentType: string,
): 'brand' | 'important' | 'informative' {
  switch (agentType) {
    case 'PA':
      return 'brand';
    case 'MA':
      return 'important';
    default:
      return 'informative';
  }
}

export function InteractionSummary({ data }: InteractionSummaryProps) {
  const styles = useStyles();

  const { passed, total } = getSkillPassCount(data);
  const allPass = passed === total && total > 0;
  const evalAvg = getEvalAverage(data);

  const formattedTime = data.creationTime
    ? new Date(data.creationTime).toLocaleString()
    : '\u2014';
  const utcTime = data.creationTime
    ? new Date(data.creationTime).toISOString()
    : '';

  const hasHtml = /<[a-z][\s\S]*>/i.test(data.response);
  const isMd = !hasHtml && isMarkdownContent(data.response);

  return (
    <Card className={styles.root}>
      <Title2>Interaction Summary</Title2>

      {/* Triage strip */}
      <div className={styles.triageStrip}>
        <Badge appearance="filled" color={agentBadgeColor(data.agentType)}>
          {data.friendlyAgentName}
        </Badge>
        {data.environment.deployRing && (
          <Badge appearance="tint" color="informative">
            {data.environment.deployRing}
          </Badge>
        )}
        {total > 0 && (
          <Badge
            appearance="tint"
            color={allPass ? 'success' : 'danger'}
            icon={allPass ? <Checkmark16Filled /> : <Dismiss16Filled />}
          >
            Skills {passed}/{total}
          </Badge>
        )}
        {evalAvg != null && (
          <Badge
            appearance="tint"
            color={
              evalAvg >= 5 ? 'success' : evalAvg >= 4 ? 'warning' : 'danger'
            }
          >
            Eval {evalAvg.toFixed(1)}
          </Badge>
        )}
        {data.isTestRequest && (
          <Badge appearance="filled" color="warning">
            TEST
          </Badge>
        )}
      </div>

      {/* Meta */}
      <div className={styles.metaRow}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>CV</span>
          <span className={styles.metaValue}>{data.cv}</span>
          <InlineCopy value={data.cv} label="CV" />
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Request ID</span>
          <span className={styles.metaValue}>{data.requestId}</span>
          <InlineCopy value={data.requestId} label="Request ID" />
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Time</span>
          <Tooltip content={utcTime} relationship="description">
            <span className={styles.metaValue}>{formattedTime}</span>
          </Tooltip>
        </div>
        {data.region && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>User Timezone</span>
            <span className={styles.metaValue}>{data.region}</span>
          </div>
        )}
      </div>

      {/* User Prompt */}
      <div className={styles.promptSection}>
        <span className={styles.promptLabel}>User Prompt</span>
        <div className={styles.promptBox}>{data.userPrompt || '(empty)'}</div>
      </div>

      {/* Copilot Response */}
      <div className={styles.promptSection}>
        <span className={styles.promptLabel}>Agent Response</span>
        {hasHtml ? (
          <div
            className={styles.responseBoxRendered}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(data.response),
            }}
          />
        ) : isMd ? (
          <div
            className={styles.responseBoxRendered}
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(data.response || '(empty)'),
            }}
          />
        ) : (
          <div className={styles.responseBox}>{data.response || '(empty)'}</div>
        )}
      </div>
    </Card>
  );
}
